/**
* DevExpress Dashboard (parameter-dialog-extension.js)
* Version:  24.1.7
* Build date: Oct 29, 2024
* Copyright (c) 2012 - 2024 Developer Express Inc. ALL RIGHTS RESERVED
* License: https://www.devexpress.com/Support/EULAs/universal.xml
*/
import * as ko from 'knockout';
import { $unwrap } from '../../data/_jquery-helpers';
import { DisposableObject } from '../../model/disposable-object';
import { defineObsoleteMethod } from '../../model/internal/_obsolete-helper';
import { DashboardLayoutModeHelper } from '../../viewer-parts/_dashboard-layout-mode-helper';
import { parametersDialog } from '../../viewer-parts/widgets/dialogs/_parameters-dialog';
import { defaultExtensions, extensionNameMap } from '../control-options';
import { OptionsManager } from '../internal/_options-manager';
import { ParameterDialogViewModel } from './_parameter-dialog-binder';
const name = 'dashboardParameterDialog';
const nameAlias = 'dashboard-parameter-dialog';
export class DashboardParameterDialogExtension extends DisposableObject {
    get onShowing() {
        return this._onShowing;
    }
    set onShowing(value) {
        if (this._onShowing) {
            this.off('showing', this._onShowing);
        }
        this._onShowing = value;
        this.on('showing', value);
    }
    get onShown() {
        return this._onShown;
    }
    set onShown(value) {
        if (this._onShown) {
            this.off('shown', this._onShown);
        }
        this._onShown = value;
        this.on('shown', value);
    }
    get onHidden() {
        return this._onHidden;
    }
    set onHidden(value) {
        if (this._onHidden) {
            this.off('hidden', this._onHidden);
        }
        this._onHidden = value;
        this.on('hidden', value);
    }
    constructor(dashboardControl, options) {
        super();
        this._customDialogContent = [];
        this._optionsManager = new OptionsManager();
        this.name = name;
        this._isLoading = false;
        this.showDialogButton = ko.observable(true);
        this._updateViewModel = (dashboard) => {
            this._clear();
            if (!!dashboard) {
                this._viewModel = new ParameterDialogViewModel(dashboard.parameters, (parameterType, settings, forceRequest, tempParameterValues) => this._dashboardControl._dataSourceBrowser.getParameterValues(parameterType, settings, forceRequest, tempParameterValues));
                this._viewModel.parameterCollection.subscribe((newParamCollection) => {
                    const parameters = (this._viewModel.parameterCollection && this._viewModel.parameterCollection()._parameters) ?? [];
                    const newParameters = newParamCollection?._parameters ?? [];
                    if (this._shouldResetCustomDialogContent(parameters, newParameters)) {
                        this._clearContent();
                        this._customDialogContent.concat(this._parameterDialog)
                            .filter(dialog => dialog && dialog.setActualState)
                            .forEach(dialog => dialog.setActualState());
                    }
                });
            }
        };
        this._optionsManager.initialize({
            extensionName: name,
            dashboardControl: dashboardControl,
            defaultOptions: {},
            eventsHolder: this,
            initOptions: options,
            optionChanged: (args) => null
        });
        this._dashboardControl = dashboardControl;
        this._onShowing = this._optionsManager.getInitialOptions().onShowing || (() => { });
        this._onShown = this._optionsManager.getInitialOptions().onShown || (() => { });
        this._onHidden = this._optionsManager.getInitialOptions().onHidden || (() => { });
    }
    start() {
        if (this._dashboardControl.dashboard()) {
            this._updateViewModel(this._dashboardControl.dashboard());
            this._subscribeDynamicLookUpValuesLoaded();
        }
        this.toDispose(this._dashboardControl.dashboard.subscribe(newDashboard => this._updateViewModel(newDashboard)));
        this.toDispose(this._dashboardControl.dashboard.subscribe(() => this._subscribeDynamicLookUpValuesLoaded()));
        defineObsoleteMethod({
            target: this,
            memberName: 'showDialog',
            oldMemberDisplayName: 'DashboardParameterDialogExtension.showDialog',
            newMemberDisplayName: 'DashboardParameterDialogExtension.show',
            action: () => this.show()
        });
        defineObsoleteMethod({
            target: this,
            memberName: 'hideDialog',
            oldMemberDisplayName: 'DashboardParameterDialogExtension.hideDialog',
            newMemberDisplayName: 'DashboardParameterDialogExtension.hide',
            action: () => this.hide()
        });
    }
    stop() {
        this._clear();
        this.dispose();
    }
    show() {
        if (this._parameterDialog) {
            this._parameterDialog.dispose();
        }
        this._parameterDialog = this._createParameterDialog();
        this._parameterDialog.show();
    }
    hide() {
        this._isLoading = false;
        this._parameterDialog.hideLoadPanel();
        if (!!this._parameterDialog) {
            this._parameterDialog.hide();
        }
    }
    subscribeToContentChanges(callback) {
        return this._viewModel.parameterCollection.subscribe(callback);
    }
    getParameters() {
        return this._getParameters();
    }
    _getParameters() {
        if (!this._viewModel)
            throw 'Dashboard is not loaded';
        return this._viewModel.parameterCollection();
    }
    renderContent(element) {
        var customParameterDialog = this._createParameterDialog();
        this._customDialogContent.push(customParameterDialog);
        return customParameterDialog.generateContent($unwrap(element), () => {
            this._customDialogContent.splice(this._customDialogContent.indexOf(customParameterDialog), 1);
        });
    }
    updateCascadingParameters(parameterName, selectedValue, parameterEntitiesDict, isMasterParameter = true, parameterDialogExtension) {
        if (isMasterParameter && parameterDialogExtension._isLoading)
            return;
        let tempParameterValues = parameterDialogExtension._dashboardControl.dashboardContainer().dashboard._tempParameterValues;
        if (isMasterParameter && tempParameterValues[parameterName]?.toString() === selectedValue?.toString())
            return;
        parameterDialogExtension._isLoading = true;
        const parameterCollection = parameterDialogExtension._viewModel.parameterCollection.peek();
        const parameters = parameterCollection.getParameterList();
        const updatedParameterIndex = parameters.findIndex(parameter => parameter.getName() === parameterName);
        if (updatedParameterIndex < 0 || updatedParameterIndex === parameters.length - 1) {
            parameterDialogExtension._isLoading = false;
            parameterDialogExtension._parameterDialog?.hideLoadPanel();
            return;
        }
        if (isMasterParameter) {
            tempParameterValues = {};
            for (let i = 0; i <= updatedParameterIndex; ++i) {
                let paramName = parameters[i].getName();
                const parameterValue = parameterEntitiesDict[paramName]?.getValue();
                if (parameterValue)
                    tempParameterValues[paramName] = parameterValue;
            }
        }
        tempParameterValues[parameterName] = selectedValue;
        parameterDialogExtension._dashboardControl.dashboardContainer().dashboard._tempParameterValues = tempParameterValues;
        const currentParameter = parameters[updatedParameterIndex + 1];
        const currentParameterEntity = parameterEntitiesDict[currentParameter.getName()];
        const dynamicListLookUpSettings = currentParameter.dynamicListLookUpSettings && currentParameter.dynamicListLookUpSettings();
        let dynamicLookupValues;
        let invalidDynamicLookUpSettings = false;
        if (dynamicListLookUpSettings) {
            parameterDialogExtension._parameterDialog?.showLoadPanel();
            if (!dynamicListLookUpSettings.valueMemberName() && !dynamicListLookUpSettings.displayMemberName())
                invalidDynamicLookUpSettings = true;
            else
                dynamicLookupValues = parameterDialogExtension._viewModel._getParameterValuesCallback(currentParameter.originalType, dynamicListLookUpSettings, true, tempParameterValues);
        }
        const updateParameterValues = (newValue, parameterEntity) => {
            parameterEntitiesDict[parameterEntity.name].setLookUpValues(newValue.map(value => {
                return {
                    getDisplayText: () => value.DisplayText,
                    getValue: () => value.Value
                };
            }));
            if (parameterEntity.allowMultiselect) {
                let newSelectedValues = [];
                if (parameterEntity.selectAllValues) {
                    newSelectedValues = newValue.map(lookUpValue => lookUpValue.Value);
                }
                else {
                    const selectedValues = parameterEntity.getValue();
                    if (!selectedValues)
                        return;
                    newSelectedValues = selectedValues.filter(selectedValue => newValue.some(item => item.Value === selectedValue));
                }
                parameterEntity.setValue(newSelectedValues);
                tempParameterValues[parameterEntity.name] = newSelectedValues;
            }
            else {
                if (newValue.length) {
                    if (!newValue.some(lookUpValue => lookUpValue.Value === parameterEntity.getValue())) {
                        parameterEntity.allowNull ? parameterEntity.setValue(null) :
                            parameterEntity.setValue(newValue[0].Value);
                        tempParameterValues[parameterEntity.name] = newValue[0].Value;
                    }
                }
            }
        };
        const moveToNextParameter = () => parameterDialogExtension.updateCascadingParameters(currentParameterEntity.name, currentParameterEntity.getValue(), parameterEntitiesDict, false, parameterDialogExtension);
        if (!dynamicLookupValues || !currentParameterEntity || invalidDynamicLookUpSettings) {
            moveToNextParameter();
            return;
        }
        parameterDialogExtension._disposables.push(dynamicLookupValues.subscribe((value) => {
            updateParameterValues(value, currentParameterEntity);
            moveToNextParameter();
        }));
    }
    _createParameterDialog() {
        return new parametersDialog({
            parametersDialogContainer: DashboardLayoutModeHelper.isMobile ? window.document.body : this._dashboardControl.getWidgetContainer(),
            getParametersCollection: () => this._getParameters(),
            submitParameters: (newParameters) => {
                if (this._parameterDialog)
                    this._parameterDialog.hideLoadPanel();
                this._dashboardControl._dataSourceBrowser?.clearDimensionValuesCache();
                this._dashboardControl.dashboardContainer().dashboard._tempParameterValues = {};
                this._viewModel.setParameters(newParameters);
            },
            onShowing: (args) => this._optionsManager.raiseEvent('showing', args),
            onShown: (args) => this._optionsManager.raiseEvent('shown', args),
            onHidden: (args) => {
                this._optionsManager.raiseEvent('hidden', args);
                this._dashboardControl.dashboardContainer().dashboard._tempParameterValues = {};
            },
            updateCascadingParameters: (parameterName, selectedValue, parameterEntitiesDict, isMasterParameter = true) => this.updateCascadingParameters(parameterName, selectedValue, parameterEntitiesDict, isMasterParameter, this)
        });
    }
    _clearContent() {
        this._customDialogContent.forEach(dialog => dialog.dispose());
        this._customDialogContent = [];
    }
    _clear() {
        this._clearContent();
        if (this._parameterDialog) {
            this._parameterDialog.dispose();
            this._parameterDialog = undefined;
        }
        if (this._viewModel) {
            this._viewModel.dispose();
            this._viewModel = undefined;
        }
    }
    _subscribeDynamicLookUpValuesLoaded() {
        var dataSourceBrowser = this._dashboardControl._dataSourceBrowser;
        if (dataSourceBrowser) {
            dataSourceBrowser.dynamicLookUpValuesLoaded = (dynamicListLookUpSettings) => {
                this._dashboardControl
                    .dashboard()
                    .parameters()
                    .filter(param => {
                    return param.dynamicListLookUpSettings() && param.dynamicListLookUpSettings().equals(dynamicListLookUpSettings);
                })
                    .map(param => param.name())
                    .forEach(paramName => {
                    this._optionsManager.raiseEvent('dynamicLookUpValuesLoaded', { parameterName: paramName });
                });
            };
        }
    }
    _shouldResetCustomDialogContent(parameters, newParameters) {
        if (parameters.length !== newParameters.length)
            return true;
        for (const p of parameters) {
            const newParam = newParameters.find(np => np._name === p._name);
            if (!newParam || newParam._value !== p._value)
                return true;
        }
        return false;
    }
}
defaultExtensions[nameAlias] = (dashboardControl, options) => new DashboardParameterDialogExtension(dashboardControl, options);
extensionNameMap[nameAlias] = name;
